/**
 * EFFECTIVE LOAD — the one per-ticket workload mapping the Dayboard's load
 * indicator and OBLI use, shared so the Calendar cannot say "80% loaded"
 * while the Dayboard says "105%" about the same tickets and technicians
 * (fix/foh-optiboard-calendar-truth).
 *
 *   effective minutes = getDerivedTicketTime(...).workloadMinutes
 *     book minutes adjusted by the assigned technician's efficiency and
 *     buffer (efficiency defaults 100 %; buffer via resolvePlanningBufferPct — unrecorded is 0 %, B-W4), plus live run-long / bay-blocking pause
 *     when work has started; parked and held-in-parking work adds 0 — the
 *     Dayboard's live-occupancy doctrine (Bucket 4.5 §4.5.8).
 *
 * For a future day nothing has started, so the same call yields the planned
 * part only (techOptiTime) — the scheduling equivalent, no separate formula.
 *
 * BOOK TIME is a different number (raw predicted_minutes, what was sold) and
 * is not computed here.
 */
import { resolvePlanningBufferPct } from '@/utils/planningBuffer';
import { getDerivedTicketTime, sumWorkloadMinutes } from '@/utils/derivedTicketTime';

/** The technician fields the mapping reads (a `technicians` row). */
export interface EffectiveLoadTechnician {
  id: string;
  efficiency_percentage?: number | null;
  buffer_percentage?: number | null;
  recurring_breaks?: unknown;
  non_recurring_breaks?: unknown;
  shift_start?: string | null;
  shift_end?: string | null;
}

/** One ticket's effective workload minutes, exactly as the Dayboard computes it. */
export function effectiveTicketWorkload(
  ticket: Record<string, any>,
  tech: EffectiveLoadTechnician | null | undefined,
  nowMs: number,
): ReturnType<typeof getDerivedTicketTime> {
  const actualStartIso = ticket.actual_start_time as string | null | undefined;
  const liveElapsedSeconds = actualStartIso
    ? Math.max(0, Math.floor((nowMs - new Date(actualStartIso).getTime()) / 1000))
    : 0;
  const startIso = ticket.start_time as string | null | undefined;
  const startInstant = startIso ? new Date(startIso) : null;
  return getDerivedTicketTime({
    ticket: ticket as never,
    technician: tech
      ? {
          efficiencyPercentage: tech.efficiency_percentage ?? 100,
          bufferPercentage: resolvePlanningBufferPct(tech.buffer_percentage),
          recurring_breaks: (tech.recurring_breaks ?? null) as never,
          non_recurring_breaks: (tech.non_recurring_breaks ?? null) as never,
          shift_start: tech.shift_start ?? null,
          shift_end: tech.shift_end ?? null,
        } as never
      : null,
    jobs: (ticket.ticket_jobs || ticket.jobs) as never,
    shopConfig: { willingnessRollPastEodMin: 0, eodRiskBandMin: 30 },
    startInstant: startInstant && Number.isFinite(startInstant.getTime()) ? startInstant : null,
    nowInstant: new Date(nowMs),
    liveElapsedSeconds,
  });
}

/** Σ effective workload over `tickets` (technician looked up by assigned_tech_id). */
export function sumEffectiveWorkload(
  tickets: ReadonlyArray<Record<string, any>>,
  techById: ReadonlyMap<string, EffectiveLoadTechnician>,
  nowMs: number,
): number {
  return sumWorkloadMinutes(tickets.map((t) =>
    effectiveTicketWorkload(t, t.assigned_tech_id ? techById.get(t.assigned_tech_id) ?? null : null, nowMs)));
}

/**
 * Does this technician's time count as MECHANICAL repair capacity? The
 * authority is the database's work-role answer (shop_mechanical_technician_ids:
 * TECHNICIAN family, multi-role included, legacy no-roles counted); `null`
 * means it could not be read, and then nobody is excluded.
 */
export function countsAsMechanicalCapacity(
  tech: { id: string } | null | undefined,
  mechanicalTechnicianIds: ReadonlySet<string> | null | undefined,
): boolean {
  if (!tech) return false;
  return !mechanicalTechnicianIds || mechanicalTechnicianIds.has(String(tech.id));
}
